<template>
    <div class="report-form">
        <!-- Rating -->
        <div class="report-section">
            <div class="report-q">How did the class go?</div>
            <div class="report-hint">One tap.</div>
            <div class="text-center mt-3">
                <v-rating v-model="form.rating" color="primary" background-color="muted lighten-2" hover length="5" size="32" half-increments half-icon="mdi-star-half-full"></v-rating>
            </div>
        </div>
        <v-divider class="my-4"></v-divider>

        <!-- Class activity chips -->
        <div class="report-section">
            <div class="report-q">What did you work on today?</div>
            <div class="report-hint">Tap all that apply — a small detail box appears under each one.</div>
            <div class="d-flex flex-wrap mt-3 report-chip-row">
                <v-chip v-for="chip in chipGroup.activities" :key="chip.key"
                    :outlined="!isActivitySelected(chip.key)"
                    :color="isActivitySelected(chip.key) ? 'primary' : ''"
                    :text-color="isActivitySelected(chip.key) ? 'white' : ''"
                    @click="toggleActivity(chip)"
                    class="report-chip ma-1">{{ chip.label }}</v-chip>
            </div>
            <div v-for="chip in form.activityChips" :key="'da-' + chip.key" class="d-flex align-center flex-wrap mt-3 report-detail-row">
                <div class="primary--text font-weight-bold report-detail-label">
                    {{ chip.label }}
                    <span class="report-detail-required">*</span>
                </div>
                <v-text-field v-model="chip.detail" :placeholder="chip.placeholder" dense outlined hide-details
                    :class="['report-detail-input', { 'report-detail-input--empty': isDetailEmpty(chip) }]"></v-text-field>
            </div>
            <v-text-field v-model="form.activityOther"
                placeholder="+ Add something else you covered (optional)"
                dense outlined hide-details class="report-add-other"></v-text-field>
        </div>
        <v-divider class="my-4"></v-divider>

        <!-- Engagement -->
        <div class="report-section">
            <div class="report-q">How was the student today?</div>
            <div class="report-hint">Pick one engagement level.</div>
            <div class="d-flex flex-wrap mt-3 report-chip-row">
                <v-chip v-for="level in engagementChips" :key="level"
                    :outlined="form.engagement !== level"
                    :color="form.engagement === level ? 'primary' : ''"
                    :text-color="form.engagement === level ? 'white' : ''"
                    @click="toggleEngagement(level)"
                    class="report-chip ma-1">{{ level }}</v-chip>
            </div>
        </div>
        <v-divider class="my-4"></v-divider>

        <!-- Homework chips -->
        <div class="report-section">
            <div class="report-q">Homework for next class</div>
            <div class="report-hint">Tap all that apply — a detail box appears under each one.</div>
            <div class="d-flex flex-wrap mt-3 report-chip-row">
                <v-chip v-for="chip in chipGroup.homework" :key="chip.key"
                    :outlined="!isHomeworkSelected(chip.key)"
                    :color="isHomeworkSelected(chip.key) ? 'primary' : ''"
                    :text-color="isHomeworkSelected(chip.key) ? 'white' : ''"
                    @click="toggleHomework(chip)"
                    class="report-chip ma-1">{{ chip.label }}</v-chip>
            </div>
            <div v-for="chip in form.homeworkChips" :key="'dh-' + chip.key" class="d-flex align-center flex-wrap mt-3 report-detail-row">
                <div class="primary--text font-weight-bold report-detail-label">
                    {{ chip.label }}
                    <span class="report-detail-required">*</span>
                </div>
                <v-text-field v-model="chip.detail" :placeholder="chip.placeholder" dense outlined hide-details
                    :class="['report-detail-input', { 'report-detail-input--empty': isDetailEmpty(chip) }]"></v-text-field>
            </div>
            <v-text-field v-model="form.homeworkOther"
                placeholder="+ Add something else for homework (optional)"
                dense outlined hide-details class="report-add-other"></v-text-field>
        </div>
        <v-divider class="my-4"></v-divider>

        <!-- Private note -->
        <div class="report-section">
            <div class="report-q">Private note <span class="font-weight-regular text--secondary" style="font-size: 12px;">(optional — only you see this)</span></div>
            <v-text-field v-model="form.notes" placeholder="e.g. video link, or a reminder for yourself"
                dense outlined hide-details class="mt-2"></v-text-field>
        </div>

        <v-alert v-if="errors.length" dense small type="warning" class="mt-4 mb-0">
            <div class="medium-text font-weight-medium">{{ errors[0] }}</div>
            <div v-if="errors.length > 1" class="caption mt-1">Also: {{ errors.slice(1).join(' · ') }}</div>
        </v-alert>
    </div>
</template>

<script>
import {
    getGroupForCourse,
    ENGAGEMENT_CHIPS,
    composeClassActivity,
    composeHomework,
    parseActivityProse,
    parseHomeworkProse,
} from '@/helpers/class-report-chips'

function emptyForm(){
    return {
        rating: '',
        activityChips: [],
        activityOther: '',
        engagement: '',
        homeworkChips: [],
        homeworkOther: '',
        notes: '',
    };
}

export default {
    name: 'ClassReportForm',
    props: {
        courseName: { type: String, default: '' },
        initialData: { type: Object, default: null },
        disabled: { type: Boolean, default: false },
    },
    data(){
        return { form: emptyForm() };
    },
    computed: {
        chipGroup(){
            return getGroupForCourse(this.courseName);
        },
        engagementChips(){
            return ENGAGEMENT_CHIPS;
        },
        errors(){
            const errs = [];
            const hasActivity = this.form.activityChips.length > 0
                || String(this.form.activityOther || '').trim().length > 0;
            const hasHomework = this.form.homeworkChips.length > 0
                || String(this.form.homeworkOther || '').trim().length > 0;
            if (!hasActivity) errs.push('Pick at least one activity (or add a custom one below)');
            if (!this.form.engagement) errs.push('Pick an engagement level');
            if (!hasHomework) errs.push('Pick at least one homework item (or add a custom one below)');

            const emptyActivityDetails = this.form.activityChips
                .filter(c => !String(c.detail || '').trim())
                .map(c => c.label);
            const emptyHomeworkDetails = this.form.homeworkChips
                .filter(c => !String(c.detail || '').trim())
                .map(c => c.label);
            if (emptyActivityDetails.length){
                errs.push(`Add a detail for: ${emptyActivityDetails.join(', ')}`);
            }
            if (emptyHomeworkDetails.length){
                errs.push(`Add a detail for: ${emptyHomeworkDetails.join(', ')}`);
            }
            return errs;
        },
        canSave(){
            return this.errors.length === 0;
        },
    },
    watch: {
        canSave: {
            handler(v){ this.$emit('valid', v); },
            immediate: true,
        },
        initialData: {
            handler(val){
                if (val && (val.class_activity || val.homework || val.rating || val.teacher_notes || val.notes)){
                    this.seedFromData(val);
                }
            },
            immediate: true,
        },
        courseName(){
            // If the course changes and we have seed data, re-parse against the new group.
            if (this.initialData){
                this.seedFromData(this.initialData);
            }
        },
    },
    methods: {
        seedFromData(data){
            const group = this.chipGroup;
            const activity = parseActivityProse(data.class_activity, group);
            const homework = parseHomeworkProse(data.homework, group);
            this.form = {
                rating: data.rating || '',
                activityChips: activity.activityChips,
                activityOther: activity.activityOther,
                engagement: activity.engagement,
                homeworkChips: homework.homeworkChips,
                homeworkOther: homework.homeworkOther,
                notes: data.teacher_notes != null ? data.teacher_notes : (data.notes || ''),
            };
        },
        toggleActivity(chip){
            const idx = this.form.activityChips.findIndex(c => c.key === chip.key);
            if (idx >= 0){
                this.form.activityChips.splice(idx, 1);
            } else {
                this.form.activityChips.push({
                    key: chip.key,
                    label: chip.label,
                    placeholder: chip.placeholder,
                    detail: '',
                });
            }
        },
        toggleHomework(chip){
            const idx = this.form.homeworkChips.findIndex(c => c.key === chip.key);
            if (idx >= 0){
                this.form.homeworkChips.splice(idx, 1);
            } else {
                this.form.homeworkChips.push({
                    key: chip.key,
                    label: chip.label,
                    placeholder: chip.placeholder,
                    detail: '',
                });
            }
        },
        toggleEngagement(level){
            this.form.engagement = this.form.engagement === level ? '' : level;
        },
        isActivitySelected(key){
            return this.form.activityChips.some(c => c.key === key);
        },
        isHomeworkSelected(key){
            return this.form.homeworkChips.some(c => c.key === key);
        },
        isDetailEmpty(chip){
            return !String((chip && chip.detail) || '').trim();
        },
        getPayload(){
            return {
                rating: this.form.rating,
                class_activity: composeClassActivity(
                    this.form.activityChips,
                    this.form.activityOther,
                    this.form.engagement
                ),
                homework: composeHomework(
                    this.form.homeworkChips,
                    this.form.homeworkOther
                ),
                notes: this.form.notes,
                teacher_notes: this.form.notes,
            };
        },
        reset(){
            this.form = emptyForm();
        },
    },
};
</script>

<style scoped>
.report-form {
    padding-top: 4px;
    padding-bottom: 8px;
}
.report-form .report-section {
    padding: 12px 0 4px;
}
.report-form .report-q {
    font-size: 15px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.87);
    letter-spacing: -0.005em;
    margin-bottom: 4px;
}
.report-form .report-hint {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.55);
    line-height: 1.45;
    margin-bottom: 4px;
}

.report-form >>> .v-divider {
    border-color: rgba(0, 0, 0, 0.06) !important;
}

.report-form .report-chip-row {
    margin: 8px -4px 0;
}
.report-form .report-chip {
    cursor: pointer;
    user-select: none;
    height: 34px !important;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0;
    transition: transform 0.12s ease, box-shadow 0.18s ease, border-color 0.15s ease;
}
.report-form .report-chip.v-chip--outlined {
    border-width: 1.5px;
    background: #fff !important;
}
.report-form .report-chip.v-chip--outlined:hover {
    border-color: var(--v-primary-base) !important;
    color: var(--v-primary-base) !important;
}
.report-form .report-chip:not(.v-chip--outlined) {
    font-weight: 600;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}
.report-form .report-chip:active {
    transform: scale(0.97);
}

.report-form .report-detail-row {
    gap: 14px;
    padding: 0;
    margin-top: 12px !important;
    align-items: flex-start;
}
.report-form .report-detail-label {
    font-size: 13px;
    font-weight: 700;
    color: var(--v-primary-base, #c27b09);
    width: 180px;
    flex: 0 0 180px;
    line-height: 1.35;
    padding-top: 11px;
}
.report-form .report-detail-input {
    flex: 1 1 240px;
    min-width: 0;
}
.report-form .report-detail-required {
    color: #d32f2f;
    margin-left: 2px;
    font-weight: 700;
}
.report-form .report-detail-input--empty >>> fieldset {
    border-color: #f0b73a !important;
}
.report-form .report-detail-input--empty >>> .v-input__slot {
    background: #fff7e3 !important;
}
.report-form .report-detail-input >>> .v-input__slot {
    background: #fff9f0 !important;
    border-radius: 9px;
    min-height: 40px !important;
}
.report-form .report-detail-input >>> fieldset {
    border-color: #ece3d0 !important;
    border-width: 1.5px;
    border-radius: 9px;
}
.report-form .report-detail-input >>> .v-text-field__slot input {
    color: #3c2517;
    font-size: 14px;
}
.report-form .report-detail-input >>> input::placeholder {
    color: #8a7a66;
    opacity: 0.85;
}
.report-form .report-detail-input.v-input--is-focused >>> fieldset,
.report-form .report-detail-input >>> .v-input--is-focused fieldset {
    border-color: var(--v-primary-base, #c27b09) !important;
}

.report-form .report-add-other {
    margin-top: 14px !important;
}
.report-form .report-add-other >>> .v-input__slot {
    background: #fafafa !important;
    border-radius: 9px;
}
.report-form .report-add-other >>> fieldset {
    border-style: dashed !important;
    border-color: rgba(0, 0, 0, 0.18) !important;
    border-radius: 9px;
}
.report-form .report-add-other >>> input {
    font-size: 13.5px;
}
.report-form .report-add-other >>> input::placeholder {
    color: rgba(0, 0, 0, 0.48);
    font-style: italic;
}

.report-form >>> .v-text-field--outlined fieldset {
    border-color: rgba(0, 0, 0, 0.14);
}

.report-form .v-rating {
    padding: 4px 0 2px;
}

@media (max-width: 600px) {
    .report-form .report-detail-row {
        flex-direction: column;
        gap: 4px;
    }
    .report-form .report-detail-label {
        width: auto;
        flex: 0 0 auto;
        padding-top: 0;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
    }
}
</style>
